import { useEffect, useState } from "react";
import { fetchRealEstateRunById } from "../../../../services/realEstateSourceControls";
import { formatCompletedTime } from "../../intelligence/intelligenceLabels";
import SourceHealthBadge from "../SourceHealthBadge";
import StatusBadge from "../../intelligence/StatusBadge";
import { trustScoreTone } from "../../../../utils/sourceTrustLabels";

function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms)) {
    return "—";
  }

  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

function MessageList({ items, emptyLabel }) {
  if (!items?.length) {
    return <p className="muted">{emptyLabel}</p>;
  }

  return (
    <ul className="admin-re-message-list">
      {items.map((item, index) => (
        <li key={`${item.message}-${index}`}>{item.message || String(item)}</li>
      ))}
    </ul>
  );
}

const RealEstateRunDetailModal = ({ runId, source, isOpen, onClose }) => {
  const [run, setRun] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!isOpen || !runId) {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        const data = await fetchRealEstateRunById(runId);
        if (!cancelled) {
          setRun(data.run || null);
        }
      } catch (error) {
        if (!cancelled) {
          setRun(null);
          setErrorMessage(error.message || "Unable to load run details.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, runId]);

  if (!isOpen) {
    return null;
  }

  const title = source?.displayName || run?.sourceName || "Pipeline run";

  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide"
        role="dialog"
        aria-labelledby="run-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Run detail</p>
            <h2 id="run-detail-title">{title}</h2>
            {run?.sourceKey ? (
              <p className="muted admin-re-source-code">{run.sourceKey}</p>
            ) : null}
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="admin-re-modal-body">
          {isLoading ? <p className="muted">Loading run details…</p> : null}
          {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}

          {!isLoading && !errorMessage && run ? (
            <>
              <section className="admin-re-run-detail-section">
                <h3>Run summary</h3>
                <div className="admin-re-run-detail-grid">
                  <div>
                    <span className="eyebrow">Status</span>
                    <SourceHealthBadge status={run.status?.toLowerCase() || "unknown"} />
                  </div>
                  <div>
                    <span className="eyebrow">Run ID</span>
                    <code>{run.runId}</code>
                  </div>
                  <div>
                    <span className="eyebrow">Triggered by</span>
                    <p>{run.triggeredBy || "—"}</p>
                  </div>
                  <div>
                    <span className="eyebrow">Started</span>
                    <p>{formatCompletedTime(run.startedAt)}</p>
                  </div>
                  <div>
                    <span className="eyebrow">Finished</span>
                    <p>{formatCompletedTime(run.finishedAt)}</p>
                  </div>
                  <div>
                    <span className="eyebrow">Duration</span>
                    <p>{formatDuration(run.durationMs)}</p>
                  </div>
                  <div className="admin-re-run-detail-grid__full">
                    <span className="eyebrow">Source trust after this run</span>
                    {run.trustRecalculationStatus === "FAILED" ? (
                      <p className="admin-re-run-trust-warning">
                        Not recalculated — trust recalculation failed.
                        {run.trustRecalculationError ? ` ${run.trustRecalculationError}` : ""}
                      </p>
                    ) : run.trustLabelAfterRun === "Insufficient Data" ||
                      run.trustScoreAfterRun == null ? (
                      <p>
                        Unknown
                        {run.trustConfidenceAfterRun
                          ? ` · ${run.trustConfidenceAfterRun} confidence`
                          : " · Insufficient data"}
                      </p>
                    ) : run.trustLabelAfterRun ? (
                      <p>
                        <StatusBadge
                          label={`${run.trustLabelAfterRun}, ${run.trustScoreAfterRun ?? "—"}`}
                          tone={trustScoreTone(run.trustScoreAfterRun)}
                        />
                        {run.trustConfidenceAfterRun ? (
                          <span className="muted">
                            {" "}
                            · {run.trustConfidenceAfterRun} confidence
                          </span>
                        ) : null}
                        {run.trustRecalculatedAt ? (
                          <span className="muted">
                            {" "}
                            · {new Date(run.trustRecalculatedAt).toLocaleString()}
                          </span>
                        ) : null}
                      </p>
                    ) : (
                      <p className="muted">Not recalculated yet</p>
                    )}
                  </div>
                </div>
              </section>

              <section className="admin-re-run-detail-section">
                <h3>Collection results</h3>
                <div className="admin-re-run-metrics">
                  <span>Found: {run.recordsFound}</span>
                  <span>Created: {run.recordsCreated}</span>
                  <span>Updated: {run.recordsUpdated}</span>
                  <span>Skipped: {run.recordsSkipped}</span>
                  <span>Missing: {run.recordsMissingFromSource ?? 0}</span>
                  <span>Deactivated: {run.recordsDeactivated}</span>
                  <span>Review: {run.recordsFlaggedForReview}</span>
                  <span>Duplicates: {run.duplicateCandidates ?? 0}</span>
                  <span>Canonical created: {run.canonicalPropertiesCreated ?? 0}</span>
                  <span>Auto-matched: {run.sourceListingsAutoMatched ?? 0}</span>
                  <span>Linked: {run.sourceListingsLinked ?? 0}</span>
                  <span>Warnings: {run.warningCount}</span>
                  <span>Errors: {run.errorCount}</span>
                </div>
              </section>

              <section className="admin-re-run-detail-section">
                <h3>Change detection</h3>
                <div className="admin-re-run-metrics">
                  <span>Price increases: {run.priceIncreasesDetected ?? 0}</span>
                  <span>Price decreases: {run.priceDecreasesDetected ?? 0}</span>
                  <span>Price changes: {run.priceChangesDetected ?? 0}</span>
                  <span>Reactivated: {run.listingsReactivated ?? 0}</span>
                  <span>Newly missing: {run.listingsNewlyMissing ?? 0}</span>
                  <span>History events: {run.historyEventsCreated ?? 0}</span>
                  <span>Canonical price: {run.canonicalPriceChanges ?? 0}</span>
                  <span>Canonical availability: {run.canonicalAvailabilityChanges ?? 0}</span>
                </div>
              </section>

              <section className="admin-re-run-detail-section">
                <h3>Warnings</h3>
                <MessageList items={run.warnings} emptyLabel="No warnings." />
              </section>

              <section className="admin-re-run-detail-section">
                <h3>Errors</h3>
                <MessageList items={run.errors} emptyLabel="No errors." />
                {run.errorMessage ? (
                  <p className="admin-re-run-error">{run.errorMessage}</p>
                ) : null}
                {run.stackTrace ? (
                  <details className="admin-re-stack-trace">
                    <summary>Stack trace</summary>
                    <pre>{run.stackTrace}</pre>
                  </details>
                ) : null}
              </section>

              <section className="admin-re-run-detail-section">
                <h3>Log summary</h3>
                <p>{run.logSummary || "—"}</p>
              </section>

              <section className="admin-re-run-detail-section">
                <button
                  type="button"
                  className="midpoint-share-button admin-re-btn-link"
                  onClick={() => setShowRaw((current) => !current)}
                >
                  {showRaw ? "Hide raw result" : "Show raw result"}
                </button>
                {showRaw ? (
                  <pre className="admin-re-raw-json">
                    {JSON.stringify(run.rawResult || {}, null, 2)}
                  </pre>
                ) : null}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RealEstateRunDetailModal;
